import { Component, OnDestroy, OnInit, inject, Signal, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { concatMap, from, Subject, takeUntil, tap } from 'rxjs';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { TranslatePipe } from '@ngx-translate/core';
import { IImpersonation } from 'src/app/shared/interfaces/IImpersonation';
import { UserState } from 'src/app/shared/states/user.state';
import { LoadImitableUsers, StartUserImpersonation } from 'src/app/shared/states/user.actions';
import { Router } from '@angular/router';
import { LoadingComponent } from '../../../components/loading/loading.component';
import { LoadingMode } from '../../../enums/LoadingMode.enum';

@Component({
  selector: 'app-select-impersonation-dialog',
  templateUrl: './select-impersonation-dialog.component.html',
  styleUrls: ['select-impersonation-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatSelect,
    FormsModule,
    MatOption,
    MatDialogActions,
    TranslatePipe,
    LoadingComponent,
  ],
})
export class SelectImpersonationDialogComponent implements OnInit, OnDestroy {
  private dialogRef = inject<MatDialogRef<SelectImpersonationDialogComponent>>(MatDialogRef);
  private store = inject(Store);
  private router = inject(Router);

  protected isLoading: Signal<boolean> = this.store.selectSignal<boolean>(UserState.isLoading);
  protected readonly LoadingMode = LoadingMode;
  protected imitableUsers$: Signal<IImpersonation[]> = this.store.selectSignal<IImpersonation[]>(UserState.imitableUsers);
  protected selectedUser: IImpersonation;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  ngOnInit(): void {
    this.initializeImitableUsers();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  protected closeDialog(): void {
    this.dialogRef.close();
  }

  protected updateSelectedUser(selectedUser: IImpersonation): void {
    this.selectedUser = selectedUser;
  }

  protected confirm(): void {
    const dispatchAction$ = this.store.dispatch(new StartUserImpersonation(this.selectedUser));

    dispatchAction$
      .pipe(
        tap(() => {
          this.closeDialog();
        }),
        concatMap(() => from(this.router.navigate(['/home'])).pipe(tap(() => window.location.reload()))),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  protected isExpiredOrNotValidYet(impersonation: IImpersonation) {
    const validFrom = new Date(impersonation.validFrom);
    const validTo = new Date(impersonation.validTo);
    const today = new Date();
    validFrom.setHours(0);
    today.setHours(0);
    return validTo.getTime() < today.getTime() || validFrom.getTime() > today.getTime();
  }

  private initializeImitableUsers(): void {
    this.store.dispatch(new LoadImitableUsers());
  }
}
